import range from 'lodash/range';
import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import styled from 'styled-components';
import { getMonth, getYear } from '../../../utils/helper';
import { CustomDropdownStyle } from '../styledComponents/StyledCustomDropdown';
import ArrowLeft from '../svgIcons/ArrowLeft';
import ArrowRight from '../svgIcons/ArrowRight';
import BottomArrowAlt from '../svgIcons/BottomArrowAlt';

const years = range(1990, getYear(new Date()) + 1, 1);
const availableYears = range(
  new Date().getFullYear(),
  new Date().getFullYear() + 100,
);

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const StyledDatePicker = styled.div`
  .react-datepicker__header {
    border-bottom: none;
    background-color: ${({ theme }) => theme.white};
    button {
      border: none;
      background-color: white;
    }
    svg {
      color: ${({ theme }) => theme.primary};
    }
    .react-datepicker__day-name {
      height: 44px;
      width: 44px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 10px;
      line-height: 12px;
      border-radius: 10px;
      text-transform: uppercase;
      margin: 0;
    }
    .calender-month-dropdown {
      max-width: 150px;
      width: 100%;
      min-width: min-content;
      max-height: 310px;
      overflow-y: auto;
    }
  }
  .react-datepicker {
    width: 100%;
    border: none;
    border-radius: 20px;
    overflow: hidden;

    &__aria-live {
      display: none;
    }

    .react-datepicker__month-container {
      float: inherit;
    }
    .react-datepicker__month {
      margin: 8px 24px 20px 24px;
    }
    .react-datepicker__day-names {
      display: flex;
      margin: 32px 24px 0 24px;
      border-bottom: 1px solid ${({ theme }) => theme.inputBorder};
    }
    .react-datepicker__week {
      display: flex;
      justify-content: space-around;
      .react-datepicker__day {
        margin: 0;
        height: 44px;
        width: 44px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 16px;
        line-height: 20px;
        border-radius: 10px;
        color: ${({ theme }) => theme.primaryDark};
        cursor: pointer;

        &.react-datepicker__day--selected {
          background-color: ${({ theme }) => theme.primary};
          color: ${({ theme }) => theme.white};
        }
      }
    }
  }
`;

export const CalenderHeader = styled.div`
  display: flex;
  align-items: center;
  margin: 32px 0;
  gap: 10px;
  .table-dropdown.menu-toggle {
    height: 44px;
  }
  div {
    &:nth-child(3) {
      max-width: 87px;
    }
  }
`;

const DatePicker = ({
  selectedDate,
  onChange,
  onOutsideClick,
  disableDate,
  enableFrom,
  disableYear,
}) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const wrapperRef = useRef(null);

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onOutsideClick();
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef]);

  return (
    <StyledDatePicker ref={wrapperRef}>
      <ReactDatePicker
        formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
        selected={selectedDate}
        onChange={(date) => onChange(date)}
        inline
        {...(disableDate && { minDate: new Date() })}
        {...(enableFrom && !disableDate && { minDate: enableFrom })}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <CalenderHeader>
            <button
              type="button"
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
            >
              <ArrowLeft />
            </button>
            <CustomDropdownStyle
              toggleClassName="table-dropdown ms-0 table-text"
              className="custom-drop-down custom-drop-down-width table-dropdown-custom topic-dropdown bg-white"
              dropdownToggle={
                <span className="flex-center p-2 w-100 justify-content-between">
                  <h6 className="me-1 me-lg-2 mb-0 ">
                    {months[getMonth(date)]}
                  </h6>
                  <BottomArrowAlt height={24} />
                </span>
              }
              drop="down"
              menuClassName="calender-month-dropdown"
              dropdownMenu={
                <>
                  {months.map((option) => (
                    <Dropdown.Item
                      key={option}
                      onClick={() => {
                        changeMonth(months.indexOf(option));
                        const updatedDate = new Date(date);
                        updatedDate.setMonth(months.indexOf(option));
                        onChange(updatedDate);
                      }}
                    >
                      <span>{option}</span>
                    </Dropdown.Item>
                  ))}
                </>
              }
            />
            <CustomDropdownStyle
              toggleClassName="table-dropdown ms-0 table-text"
              className="custom-drop-down custom-drop-down-width table-dropdown-custom topic-dropdown bg-white"
              dropdownToggle={
                <span className="flex-center p-2 w-100 justify-content-between">
                  <h6 className="me-1 me-lg-2 mb-0 ">{getYear(date)}</h6>
                  <BottomArrowAlt height={24} />
                </span>
              }
              menuClassName="calender-month-dropdown"
              align="end"
              dropdownMenu={
                disableYear ? (
                  <>
                    {availableYears.map((option) => (
                      <Dropdown.Item
                        key={option}
                        onClick={() => {
                          changeYear(option);
                          const updatedDate = new Date(date);
                          updatedDate.setFullYear(option);
                          onChange(updatedDate);
                        }}
                      >
                        <span>{option}</span>
                      </Dropdown.Item>
                    ))}
                  </>
                ) : (
                  <>
                    {years.map((option) => (
                      <Dropdown.Item
                        key={option}
                        onClick={() => changeYear(option)}
                      >
                        <span>{option}</span>
                      </Dropdown.Item>
                    ))}
                  </>
                )
              }
            />

            <button
              type="button"
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
            >
              <ArrowRight />
            </button>
          </CalenderHeader>
        )}
      />

      <style>
        {`
        .react-datepicker__day--disabled {
        color : #c6c2c2 !important;
        cursor : not-allowed !important;
        }
        `}
      </style>
    </StyledDatePicker>
  );
};

export default DatePicker;
